/**
 * Chat Management Utilities
 *
 * Handles chat creation, deletion, switching, and management operations.
 */

export interface ChatInfo {
  chatId: string;
  preview: string;
  messageCount: number;
  createdAt: string;
}

export interface ChatManagementHooks {
  onChatIdChange?: (chatId: string | null) => void;
  onRefreshChats?: () => void;
}

/**
 * Create a new chat
 */
export const createNewChat = async (
  workproductId: string,
  workproductType: string,
  backendUrl: string,
  hooks: ChatManagementHooks,
  language?: string
): Promise<string | null> => {
  if (!workproductId) return null;

  try {

    // Call backend to create new chat
    const response = await fetch(`${backendUrl}/api/agentchat/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        workproductId,
        workproductType,
        language: language || "en",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to create chat: ${errorData.error || response.statusText}`
      );
    }

    const data = await response.json();
    const newChatId = data.chatId;

    // Update current chat via parent
    if (hooks.onChatIdChange) {
      hooks.onChatIdChange(newChatId);
    }

    // Refresh available chats list to include the new chat
    if (hooks.onRefreshChats) {
      await hooks.onRefreshChats();
    }

    return newChatId;
  } catch (error) {
    console.error("Error creating new chat:", error);
    return null;
  }
};

/**
 * Switch to a different chat
 */
export const switchToChat = (
  newChatId: string,
  hooks: ChatManagementHooks
): void => {
  if (hooks.onChatIdChange) {
    hooks.onChatIdChange(newChatId);
  }
};

/**
 * Delete a chat
 */
export const deleteChat = async (
  chatId: string,
  workproductId: string,
  backendUrl: string,
  hooks: ChatManagementHooks
): Promise<boolean> => {
  if (!chatId || !workproductId) return false;

  try {
    const response = await fetch(`${backendUrl}/api/agentchat`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        chatId,
        workproductId,
      }),
    });

    if (response.ok) {
      // Refresh the available chats list via parent
      if (hooks.onRefreshChats) {
        await hooks.onRefreshChats();
      }

      return true;
    } else {
      const errorText = await response.text();
      console.error("Failed to delete chat:", errorText);
      return false;
    }
  } catch (error) {
    console.error("Error deleting chat:", error);
    return false;
  }
};

/**
 * Ensure a chat exists before sending a message
 * Now uses backend chat creation for consistency and security
 */
export const ensureChatExists = async (
  currentChatId: string | undefined,
  workproductId: string,
  workproductType: string,
  backendUrl: string,
  hooks: ChatManagementHooks,
  language?: string
): Promise<string> => {
  // If we already have a chat ID, use it
  if (currentChatId) {
    return currentChatId;
  }

  // No active chat found, create new chat via backend
  const newChatId = await createNewChat(
    workproductId,
    workproductType,
    backendUrl,
    hooks,
    language
  );

  if (!newChatId) {
    throw new Error("Failed to create new chat");
  }

  return newChatId;
};
